import i18n from '../i18n';

/**
 * Which way a transfer moved, seen from one of its own accounts.
 *
 * A transfer between the user's own accounts is neither income nor spend.
 * Listed under one of those accounts, though, the money did arrive or it did
 * leave — and without saying which, a transfer out of a credit card read
 * exactly like a purchase on it.
 */
const sameId = (a, b) => a != null && b != null && Number(a) === Number(b);

/** 'in' | 'out' relative to `contextAccountId`, or null when there is no side. */
export function transferDirection(movement, contextAccountId) {
  if (movement?.type !== 'Transfer') return null;
  if (sameId(movement.to_account_id, contextAccountId)) return 'in';
  if (sameId(movement.from_account_id, contextAccountId)) return 'out';
  return null;
}

/**
 * True when the row has note/description content the caller's role withholds.
 *
 * The standard movement shape omits note and description outright but keeps
 * `has_note` / `has_description`, so a row with hidden text is distinguishable
 * from one that simply has none. Without the distinction both render as the
 * empty fallback, and a reader is told "no note" about a movement that has one.
 *
 * Shared rather than per-list: every surface that prints the note has to answer
 * this the same way, or the same movement reads differently on two screens.
 */
export function isPrimaryTextRestricted(movement) {
  const hasContent = movement?.has_note || movement?.has_description;
  const contentVisible = !!(movement?.note || movement?.description);
  return !!hasContent && !contentVisible;
}

/**
 * Helper function to get account display text based on movement type
 */
export const getAccountDisplay = (movement) => {
  const unknown = i18n.t('movements:fallback.unknown');
  if (movement.type === "Transfer") {
    const fromAccount = movement.from_account?.name || unknown;
    const toAccount = movement.to_account?.name || unknown;
    return `${fromAccount} → ${toAccount}`;
  }
  return movement.account?.name || unknown;
};

/**
 * Helper function to get the primary display text (note)
 */
export const getPrimaryText = (movement) => {
  return movement.note || movement.description || i18n.t('movements:fallback.noDescription');
};

// Colors for distinguishing adjacent groups
export const GROUP_COLORS = ['purple.400', 'teal.400', 'orange.400', 'pink.400', 'cyan.400'];
